import { supabase } from '../../lib/supabase'

import { extractOnboardingNamesFromAuthUser } from './authProfile'
import { OnboardingData, AIProvider } from './types'

// Load onboarding data
export function loadOnboardingData(
  setData: React.Dispatch<React.SetStateAction<OnboardingData>>,
  setResumeFilePath: React.Dispatch<React.SetStateAction<string | null>>,
  setResumeUploaded: React.Dispatch<React.SetStateAction<boolean>>,
  setResumeMdPath: React.Dispatch<React.SetStateAction<string | null>>,
  setAiProvider: React.Dispatch<React.SetStateAction<AIProvider>>,
  setAiMemoryFilePath: React.Dispatch<React.SetStateAction<string | null>>,
  setAiMemoryUploaded: React.Dispatch<React.SetStateAction<boolean>>,
  setDataLoaded: React.Dispatch<React.SetStateAction<boolean>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  navigate: (path: string) => void,
  data: OnboardingData
): () => void {
  let cancelled = false
  
  const loadData = async () => {
    try {
      // Only show loading if we don't have any data yet
      if (!data.firstName && !data.lastName && !data.email) {
        setLoading(true)
      }
    
      const { data: { user } } = await supabase.auth.getUser()
      if (cancelled) return
      
      if (!user) {
        navigate('/login')
        return
      }

      // Get user email from auth (set immediately to prevent flash)
      if (user.email) {
        setData(prev => {
          // Only update if email is not already set
          if (prev.email) return prev
          return { ...prev, email: user.email || '' }
        })
      }

      const authProfileNames = extractOnboardingNamesFromAuthUser(user)

      // Load profile data from user_profile
      const { data: profileData, error: profileError } = await supabase
        .from('user_profile')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()

      // Load onboarding status
      const { error: onboardingStatusError } = await supabase
        .from('user_onboarding')
        .select('completed_at, started_at')
        .eq('user_id', user.id)
        .maybeSingle()

      const fetchError = profileError || onboardingStatusError

      if (cancelled) return

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error loading profile/onboarding data:', fetchError)
      }

      // Load data from user_profile
      if (profileData) {
        setData({
          firstName: profileData.first_name || authProfileNames.firstName,
          lastName: profileData.last_name || authProfileNames.lastName,
          phone: profileData.phone || '',
          email: profileData.email || user.email || '',
          resumeFile: null,
        })
        if (profileData.resume_file_path) {
          setResumeFilePath(profileData.resume_file_path)
          setResumeUploaded(true)
        }
        if (profileData.resume_md_path) {
          setResumeMdPath(profileData.resume_md_path)
        }
        if (profileData.ai_provider) {
          setAiProvider(profileData.ai_provider as AIProvider)
        }
        if (profileData.ai_memory_file_path) {
          setAiMemoryFilePath(profileData.ai_memory_file_path)
          setAiMemoryUploaded(true)
        }
      } else if (authProfileNames.firstName || authProfileNames.lastName) {
        setData(prev => ({
          ...prev,
          firstName: prev.firstName || authProfileNames.firstName,
          lastName: prev.lastName || authProfileNames.lastName,
          email: prev.email || user.email || '',
        }))
      }
      
      // Mark data as loaded to prevent re-fetching
      setDataLoaded(true)
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err)
      console.error('Error loading onboarding:', errorMessage)
    } finally {
      if (!cancelled) {
        setLoading(false)
      }
    }
  }
  
  loadData()
  
  return () => {
    cancelled = true
  }
}
